import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import MeetingCard from './MeetingCard';
import BookSlotModal from './BookSlotModal';
import SpeechCard from './SpeechCard';
import LeaderboardWidget from './LeaderboardWidget';
import Larry, { LarryGreeting } from './Larry';
import { getMeetings, getSpeeches, getCurrentMember, bookSlot, cancelSlotBooking } from '../services/api';
import usePageTitle from '../hooks/usePageTitle';
import { FEATURES } from '../config/features';

const MEETINGS_LIMIT = 3;
const SPEECHES_LIMIT = 4;
const MY_SPEECHES_FETCH = 100;

/* ─── Role badge styles — amber primary, indigo for evaluators ─── */
const ROLE_BADGE = {
  admin:     { label: 'Club Admin',       style: { background: 'var(--accent-dim)',  color: 'var(--accent)',   border: '1px solid var(--border-accent)'  } },
  evaluator: { label: 'Evaluator',        style: { background: 'var(--color-info-dim)', color: 'var(--color-info)',   border: '1px solid var(--color-info-border)' } },
  speaker:   { label: 'Speaker',          style: { background: 'var(--color-success-dim)',  color: 'var(--color-success)',   border: '1px solid var(--color-success-border)'  } },
  member:    { label: 'Member',           style: { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' } },
};

export default function Dashboard() {
  usePageTitle('Dashboard');
  const [member, setMember] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [recentSpeeches, setRecentSpeeches] = useState([]);
  const [mySpeeches, setMySpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slotError, setSlotError] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const [memberRes, meetingsRes, publicRes, myRes] = await Promise.all([
        getCurrentMember().catch(() => null),
        FEATURES.SCHEDULING ? getMeetings('upcoming', MEETINGS_LIMIT) : Promise.resolve(null),
        getSpeeches('public', SPEECHES_LIMIT),
        getSpeeches('my', MY_SPEECHES_FETCH),
      ]);
      if (memberRes?.member)    setMember(memberRes.member);
      if (meetingsRes?.meetings) setUpcomingMeetings(meetingsRes.meetings);
      if (publicRes?.speeches)  setRecentSpeeches(publicRes.speeches);
      if (myRes?.speeches)      setMySpeeches(myRes.speeches);
    } catch (err) {
      console.error('[Dashboard] Load error:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotClick = async (slot) => {
    setSlotError(null);
    if (slot.status === 'open') {
      setBookingSlot(slot);
      return;
    }
    if (slot.speaker_id === member?.member_id) {
      try {
        await cancelSlotBooking(slot.slot_id);
        const res = await getMeetings('upcoming', MEETINGS_LIMIT);
        if (res?.meetings) setUpcomingMeetings(res.meetings);
      } catch (err) {
        setSlotError(err.message || 'Failed to cancel booking');
      }
    }
  };

  const handleBookingConfirm = async (presentation_title, synopsis) => {
    await bookSlot(bookingSlot.slot_id, { presentation_title, synopsis });
    setBookingSlot(null);
    const res = await getMeetings('upcoming', MEETINGS_LIMIT);
    if (res?.meetings) setUpcomingMeetings(res.meetings);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="animate-pulse rounded-2xl" style={{ background: 'var(--bg-input)', minHeight: '220px' }} />
          <div className="lg:col-span-2 animate-pulse rounded-2xl" style={{ background: 'var(--bg-input)', minHeight: '220px' }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1].map(i => (
            <div key={i} className="animate-pulse h-40 rounded-2xl" style={{ background: 'var(--bg-input)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Larry mood="sad" size={72} message="Couldn't load the dashboard." />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <button onClick={loadDashboard} className="btn-bouncy text-sm px-5 py-2">Try Again</button>
      </div>
    );
  }

  const canBook = !!member;
  const canCancel = !!member;
  const hasMySpeeches = mySpeeches.length > 0;

  return (
    <div className="space-y-8 stagger-in">
      {bookingSlot && (
        <BookSlotModal
          meetingDate={bookingSlot.meeting_date}
          onConfirm={handleBookingConfirm}
          onClose={() => setBookingSlot(null)}
        />
      )}
      {slotError && (
        <div
          className="px-4 py-3 text-sm font-medium rounded-xl flex items-center gap-2"
          style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}
        >
          <span>⚠</span>
          {slotError}
          <button
            onClick={() => setSlotError(null)}
            className="ml-auto transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
          >✕</button>
        </div>
      )}

      {/* ── BENTO ROW 1: Welcome + Featured Meeting ── */}
      <div className={FEATURES.SCHEDULING ? 'grid grid-cols-1 lg:grid-cols-3 gap-5' : 'grid grid-cols-1 gap-5'}>
        {member && <WelcomeCard member={member} speeches={mySpeeches} upcomingMeetings={upcomingMeetings} />}
        {FEATURES.SCHEDULING && (
          <div className={member ? 'lg:col-span-2' : 'lg:col-span-3'}>
            {upcomingMeetings.length > 0 ? (
              <SpotlightMeetingCard
                meeting={upcomingMeetings[0]}
                member={member}
                canBook={canBook}
                canCancel={canCancel}
                onSlotClick={handleSlotClick}
              />
            ) : (
              <NoMeetingsCard isAdmin={member?.role === 'admin'} />
            )}
          </div>
        )}
      </div>

      {/* ── Additional meetings (2nd, 3rd) ── */}
      {FEATURES.SCHEDULING && upcomingMeetings.length > 1 && (
        <section>
          <SectionMarker title="Also coming up" linkTo="/calendar" linkLabel="Full schedule" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcomingMeetings.slice(1).map((meeting) => (
              <MeetingCard
                key={meeting.meeting_id}
                meeting={meeting}
                currentMemberId={member?.member_id}
                canBook={canBook}
                canCancel={canCancel}
                onSlotClick={handleSlotClick}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── BENTO ROW 2: My Speeches + Leaderboard ── */}
      <div className={FEATURES.GAMIFICATION ? 'grid grid-cols-1 lg:grid-cols-3 gap-5' : 'grid grid-cols-1 gap-5'}>
        {hasMySpeeches && (
          <section className={FEATURES.GAMIFICATION ? 'lg:col-span-2' : ''}>
            <SectionMarker title="My speeches" linkTo="/my-speeches" linkLabel="All of them" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {mySpeeches.slice(0, 4).map((speech) => (
                <SpeechCard key={speech.speech_id} speech={speech} showSpeaker={false} showMeeting />
              ))}
            </div>
          </section>
        )}
        {FEATURES.GAMIFICATION && (
          <section className={hasMySpeeches ? 'lg:col-span-1' : 'lg:col-span-3'}>
            <LeaderboardWidget currentMemberId={member?.member_id} />
          </section>
        )}
      </div>

      {/* ── Recent Club Speeches ── */}
      <section>
        <SectionMarker title="From the club" linkTo="/browse" linkLabel="Browse" />
        {recentSpeeches.length === 0 ? (
          <div className="card-paper p-8 flex flex-col items-center text-center gap-3">
            <Larry mood="happy" size={56} message="The stage is empty." />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Be the first one to stand up.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentSpeeches.map((speech) => (
              <SpeechCard key={speech.speech_id} speech={speech} showSpeaker showMeeting />
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

/* ─────────────────────────────────────────────
   WelcomeCard — amber warmth radiates from top
───────────────────────────────────────────── */
function WelcomeCard({ member, speeches = [], upcomingMeetings = [] }) {
  const roleBadge = ROLE_BADGE[member?.role] || ROLE_BADGE.member;

  const speechCount = speeches.length;

  const nextSlot = upcomingMeetings
    .flatMap(m => (m.slots || []).map(s => ({ ...s, meeting_date: m.meeting_date })))
    .find(s => String(s.speaker_id) === String(member.member_id));

  const lastSpeech = speeches[0];

  // Sparkline: last 5 scores, oldest→newest
  const sparkScores = speeches.slice(0, 5).reverse()
    .map(s => s?.metrics?.confidence_score ?? s?.metrics?.vocabulary?.score)
    .filter(s => s != null);
  const sparkTrend = sparkScores.length >= 2
    ? sparkScores[sparkScores.length - 1] - sparkScores[0]
    : 0;
  const trendColor = sparkTrend > 5 ? 'var(--color-success)'
    : sparkTrend < -5 ? 'var(--color-danger)'
    : 'var(--accent)';
  const trendArrow = sparkTrend > 5 ? '↑' : sparkTrend < -5 ? '↓' : '→';

  const SPK_W = 56, SPK_H = 26;
  const sparkMin = sparkScores.length ? Math.min(...sparkScores) : 0;
  const sparkMax = sparkScores.length ? Math.max(...sparkScores) : 100;
  const sparkRange = (sparkMax - sparkMin) || 20;
  const sparkPts = sparkScores.map((s, i) => {
    const x = sparkScores.length === 1 ? SPK_W / 2 : (i / (sparkScores.length - 1)) * SPK_W;
    const y = SPK_H - ((s - sparkMin) / sparkRange) * (SPK_H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const daysSinceLast = lastSpeech?.created_at
    ? Math.round((Date.now() - Number(lastSpeech.created_at)) / 86400000)
    : null;
  const cadenceLabel = daysSinceLast == null ? null
    : daysSinceLast === 0 ? 'Today'
    : daysSinceLast === 1 ? 'Yesterday'
    : `${daysSinceLast}d ago`;

  const slotDate = nextSlot
    ? new Date(nextSlot.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : null;

  const hasStats = speechCount > 0 || slotDate || sparkScores.length > 0;

  return (
    <div
      className="rounded-panel p-6 relative overflow-hidden flex flex-col"
      style={{
        background: 'linear-gradient(160deg, var(--bg-input) 0%, var(--bg-canvas) 100%)',
        border: '1px solid var(--border-subtle)',
        minHeight: '220px',
      }}
    >
      {/* Accent bloom from top */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-24 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, var(--accent-glow) 0%, transparent 70%)' }}
      />
      {/* Waveform texture */}
      <WaveformDeco className="absolute bottom-0 left-0 right-0 pointer-events-none" />

      <div className="relative z-10 flex-1 flex flex-col">
        <LarryGreeting name={member.first_name} />
        <h2
          className="font-display text-2xl sm:text-[1.7rem] font-bold mt-3 leading-tight break-words"
          style={{ color: 'var(--text-primary)' }}
        >
          Good to have you,<br />
          <span className="text-gradient-amber">{member.first_name}</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-pill text-xs font-semibold"
            style={roleBadge.style}
          >
            {roleBadge.label}
          </span>
          {member.status === 'pending' && (
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-pill text-xs font-semibold"
              style={{ background: 'var(--color-warning-dim)', color: 'var(--color-warning)', border: '1px solid var(--color-warning-border)' }}
            >
              Pending
            </span>
          )}
        </div>

        {/* Stats row — pushed to bottom */}
        {hasStats && (
          <div
            className="flex flex-wrap items-center gap-4 mt-auto pt-3"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            {speechCount > 0 && (
              <div>
                <div className="text-xl font-bold font-mono leading-none" style={{ color: 'var(--text-primary)' }}>
                  {speechCount === MY_SPEECHES_FETCH ? `${speechCount}+` : speechCount}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-faint)' }}>
                  speeches
                </div>
              </div>
            )}
            {speechCount > 0 && (slotDate || sparkScores.length > 0) && (
              <div className="w-px h-7 self-center flex-shrink-0" style={{ background: 'var(--border-subtle)' }} />
            )}
            {slotDate ? (
              <div>
                <div className="text-sm font-bold font-mono leading-none" style={{ color: 'var(--accent)' }}>
                  {slotDate}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-faint)' }}>
                  on stage
                </div>
              </div>
            ) : null}
            {sparkScores.length > 0 && (
              <>
                {slotDate && <div className="w-px h-7 self-center flex-shrink-0" style={{ background: 'var(--border-subtle)' }} />}
                <div>
                  <div className="flex items-center gap-1.5">
                    <svg width={SPK_W} height={SPK_H} viewBox={`0 0 ${SPK_W} ${SPK_H}`} style={{ overflow: 'visible' }}>
                      <polyline
                        points={sparkPts}
                        fill="none"
                        stroke={trendColor}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.9"
                      />
                      {/* Last point dot */}
                      {sparkScores.length >= 1 && (() => {
                        const last = sparkPts.split(' ').pop();
                        const [lx, ly] = last.split(',').map(Number);
                        return <circle cx={lx} cy={ly} r="2.5" fill={trendColor} />;
                      })()}
                    </svg>
                    <span className="text-sm font-bold font-mono" style={{ color: trendColor }}>
                      {trendArrow}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-faint)' }}>
                    {cadenceLabel ? `${cadenceLabel}` : 'score trend'}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SpotlightMeetingCard — event poster hero
   Amber spotlight radiates from top.
   Date in Cormorant display numerals.
───────────────────────────────────────────── */
function SpotlightMeetingCard({ meeting, member, canBook, canCancel, onSlotClick }) {
  const { meeting_id, meeting_date, title, status, slots = [] } = meeting;

  const d = new Date(meeting_date);
  const weekday   = d.toLocaleDateString('en-IN', { weekday: 'long' }).toUpperCase();
  const dayNum    = d.getDate();
  const monthYear = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }).toUpperCase();

  const bookedCount = slots.filter(s => s.status !== 'open').length;
  const memberSlot  = slots.find(s => s.speaker_id === member?.member_id);
  const openSlot    = slots.find(s => s.status === 'open');

  const STATUS_C = {
    scheduled: { bg: 'var(--color-warning-dim)',  color: 'var(--color-warning)',  border: 'var(--color-warning-border)'  },
    completed: { bg: 'var(--color-success-dim)',  color: 'var(--color-success)',  border: 'var(--color-success-border)'  },
    cancelled: { bg: 'var(--color-danger-dim)',   color: 'var(--color-danger)',  border: 'var(--color-danger-border)'   },
  };
  const sc = STATUS_C[status] || STATUS_C.scheduled;

  return (
    <div
      className="rounded-panel p-6 relative overflow-hidden h-full"
      style={{
        background: 'radial-gradient(ellipse 80% 55% at 50% -8%, var(--accent-glow) 0%, transparent 65%), var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        minHeight: '220px',
      }}
    >
      {/* Status badge */}
      <span
        className="absolute top-5 right-5 px-2.5 py-0.5 rounded-pill text-xs font-semibold uppercase tracking-wider"
        style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}
      >
        {status}
      </span>

      {/* Date */}
      <div className="mb-5">
        <p
          className="font-mono text-xs font-medium tracking-[0.22em] uppercase mb-0.5"
          style={{ color: 'var(--text-faint)' }}
        >
          {weekday}
        </p>
        <p
          className="font-display font-bold leading-none"
          style={{ fontSize: 'clamp(3rem, 8vw, 4.5rem)', color: 'var(--text-primary)' }}
        >
          {dayNum}
        </p>
        <p
          className="font-mono text-xs font-medium tracking-[0.16em] mt-1"
          style={{ color: 'var(--accent)' }}
        >
          {monthYear}
        </p>
      </div>

      {/* Meeting title */}
      <p className="font-medium text-sm mb-4 leading-snug" style={{ color: 'var(--text-secondary)' }}>
        {title || 'Weekly Meeting'}
      </p>

      {/* Slot pills */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {slots.map((slot) => {
          const isYou  = slot.speaker_id === member?.member_id;
          const isOpen = slot.status === 'open';
          return (
            <div
              key={slot.slot_id}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
              style={{
                background: isYou  ? 'var(--accent-dim)'   : isOpen ? 'rgba(255,255,255,0.02)' : 'var(--color-info-dim)',
                border:     isYou  ? '1px solid var(--border-accent)' : isOpen ? '1px dashed rgba(255,255,255,0.1)' : '1px solid var(--color-info-border)',
              }}
            >
              <span className="text-[9px] font-medium" style={{ color: 'var(--text-faint)' }}>#{slot.slot_number}</span>
              <span
                className="text-xs font-semibold"
                style={{ color: isYou ? 'var(--accent)' : isOpen ? 'var(--text-faint)' : 'var(--color-info)' }}
              >
                {isYou ? 'You ✓' : isOpen ? 'Open' : (slot.speaker_name?.split(' ')[0] || 'Booked')}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-mono" style={{ color: 'var(--text-faint)' }}>
          {bookedCount} of {slots.length} taken
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {canBook && openSlot && !memberSlot && (
            <button onClick={() => onSlotClick(openSlot)} className="btn-bouncy text-xs px-4 py-2">
              Claim Slot
            </button>
          )}
          {canCancel && memberSlot && memberSlot.status === 'booked' && (
            <button
              onClick={() => onSlotClick(memberSlot)}
              className="text-xs px-3 py-2 rounded-lg font-semibold transition-colors"
              style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--color-danger)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
            >
              Cancel
            </button>
          )}
          <Link
            to={`/meetings/${meeting_id}`}
            className="text-xs font-semibold transition-colors"
            style={{ color: 'var(--accent)', opacity: 0.8 }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
          >
            Details →
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   NoMeetingsCard — empty state
───────────────────────────────────────────── */
function NoMeetingsCard({ isAdmin }) {
  return (
    <div
      className="rounded-panel flex flex-col items-center justify-center p-8 text-center h-full"
      style={{
        background: 'rgba(255,255,255,0.01)',
        border: '1px dashed var(--border-accent)',
        minHeight: '220px',
      }}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
        style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-accent)' }}
      >
        <span style={{ color: 'var(--accent)', fontSize: 18 }}>◇</span>
      </div>
      <p className="font-medium" style={{ color: 'var(--text-muted)' }}>Nothing scheduled yet.</p>
      {isAdmin ? (
        <Link to="/admin" className="btn-bouncy text-sm px-5 py-2 mt-4">+ Create meeting</Link>
      ) : (
        <>
          <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>Ask your admin to open a meeting, or check the schedule.</p>
          <Link to="/calendar" className="btn-bouncy text-sm px-5 py-2 mt-3">View schedule →</Link>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SectionMarker — editorial section label
   Amber gradient bar + mono caps + tracking
───────────────────────────────────────────── */
function SectionMarker({ title, linkTo, linkLabel }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <div
          className="w-0.5 h-3.5 rounded-full flex-shrink-0"
          style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }}
        />
        <h3
          className="font-mono text-xs font-medium tracking-[0.2em] uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {title}
        </h3>
      </div>
      {linkTo && (
        <Link
          to={linkTo}
          className="text-xs font-medium transition-colors"
          style={{ color: 'var(--text-faint)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
        >
          {linkLabel} →
        </Link>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   WaveformDeco — static SVG soundwave texture
   Amber tint — decorative DNA of this product.
───────────────────────────────────────────── */
function WaveformDeco({ className = '' }) {
  const bars = [3,5,9,14,22,32,42,52,58,56,52,44,36,28,20,14,9,12,22,34,46,54,60,56,48,36,24,14,8,5,4,3,5,9,14,20,30,42,52,58,54,44,32,20,12,7,4,3];
  return (
    <svg
      className={className}
      width="100%"
      height="48"
      preserveAspectRatio="none"
      viewBox={`0 0 ${bars.length * 8} 64`}
      aria-hidden="true"
    >
      {bars.map((h, i) => (
        <rect
          key={i}
          x={i * 8 + 2}
          y={(64 - h) / 2}
          width="4"
          height={h}
          rx="2"
          fill="var(--accent-glow)"
        />
      ))}
    </svg>
  );
}
